/* ThemeStage — sits at the very top of the React tree and reacts to
 * whichever theme the rest of the app declares "active". Paints the
 * background on <body>, mounts the floating audio player, and renders
 * the marquee strip when present.
 *
 * Why on <body>? Because the existing app already paints a base color
 * (#050505) on the outer App div; the background needs to live behind
 * everything else (including the sticky Navbar) without disrupting
 * existing z-index layers. Setting the data attr on <body> + a fixed
 * ::before underlay (in themes.css) gives us a frame-buffer-style layer
 * that we can swap on the fly per visited user.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useVaultTheme } from "./VaultThemeProvider";
import { API, ORIGIN, api } from "@/lib/api";
import toneEngine from "@/lib/toneEngine";
import { CURSORS, TRAILS } from "@/lib/cursorCatalog";
import { HIDDEN_CURSORS, HIDDEN_TRAILS } from "@/lib/hiddenCosmetics";
import CursorTrail from "./CursorTrail";
import { Play, Pause, X, Music2 } from "lucide-react";

// Combined catalogs so saved cursor/trail IDs resolve regardless of which
// catalog they come from (default vs Blackout Pulse hidden).
const ALL_CURSORS = [...CURSORS, ...HIDDEN_CURSORS];
const ALL_TRAILS = [...TRAILS, ...HIDDEN_TRAILS];

export default function ThemeStage() {
  const { active, catalog, myTheme } = useVaultTheme();
  // The theme we paint = whichever user's space we're currently visiting,
  // OR if we're not visiting anyone-specific, our OWN theme so it applies
  // across every page (homepage, archive, threads, etc.). This is the
  // "themes spread across the entire website" behavior.
  const fallbackToMine = active === null && myTheme;
  const theme = active?.theme || (fallbackToMine ? myTheme : null);
  const ownerLabel = active?.ownerLabel || (fallbackToMine ? "you" : "");

  // ----- Cursor: applied to document.documentElement so every element inherits -----
  useEffect(() => {
    const html = document.documentElement;
    if (!html) return;
    const cursor = ALL_CURSORS.find((c) => c.id === theme?.cursor_id);
    if (cursor && cursor.css && cursor.css !== "auto") {
      html.style.cursor = cursor.css;
    } else {
      html.style.removeProperty("cursor");
    }
    return () => { html.style.removeProperty("cursor"); };
  }, [theme?.cursor_id]);

  const themeTrail = useMemo(
    () => ALL_TRAILS.find((t) => t.id === theme?.trail_id) || null,
    [theme?.trail_id],
  );
  // Cursor Royale can hijack the trail recipe via a window event during its
  // active window. Falls back to the user's theme trail when the override
  // is cleared.
  const [overrideRecipe, setOverrideRecipe] = useState(null);
  useEffect(() => {
    const onOverride = (e) => setOverrideRecipe(e?.detail || null);
    window.addEventListener("vault:trail-override", onOverride);
    return () => window.removeEventListener("vault:trail-override", onOverride);
  }, []);
  const trail = overrideRecipe ? { id: "__royale__", recipe: overrideRecipe } : themeTrail;

  // ----- Text color override: pulls from the ACTIVE theme when one is set
  //       (visiting someone else's profile → see THEIR color), otherwise
  //       falls back to the signed-in visitor's own /me/profile. Applies
  //       only to page-level h1/h2/h3 (see themes.css) so body text + JS
  //       particles (cursor trail spans) stay clean. -----
  useEffect(() => {
    let alive = true;
    const html = document.documentElement;
    const clearAll = () => {
      html.removeAttribute("data-user-text");
      html.style.removeProperty("--user-text-color");
      html.style.removeProperty("--fx-gradient");
      html.style.removeProperty("--fx-animation");
      html.style.removeProperty("--fx-shadow");
      html.style.removeProperty("--fx-color");
      html.style.removeProperty("--fx-clip");
      html.style.removeProperty("--fx-weight");
      html.style.removeProperty("--fx-letter-spacing");
      html.style.removeProperty("--fx-text-transform");
    };

    const applyById = (tcId, paletteEntries) => {
      const entry = (paletteEntries || []).find((x) => x.id === tcId);
      if (!entry || !entry.hex || entry.id === "default") {
        clearAll();
        return;
      }
      if (entry.hex === "effect") {
        clearAll();
        html.setAttribute("data-user-text", entry.id);
        if (entry.gradient) {
          html.style.setProperty("--fx-gradient", entry.gradient);
          html.style.setProperty("--fx-clip", "text");
          html.style.setProperty("--fx-color", "transparent");
        } else if (entry.color) {
          html.style.setProperty("--fx-color", entry.color);
        }
        if (entry.shadow) html.style.setProperty("--fx-shadow", entry.shadow);
        if (entry.animation) html.style.setProperty("--fx-animation", entry.animation);
        if (entry.weight) html.style.setProperty("--fx-weight", entry.weight);
        if (entry.letter_spacing) html.style.setProperty("--fx-letter-spacing", entry.letter_spacing);
        if (entry.text_transform) html.style.setProperty("--fx-text-transform", entry.text_transform);
        return;
      }
      if (entry.hex === "rainbow") {
        clearAll();
        html.setAttribute("data-user-text", "rainbow");
        return;
      }
      clearAll();
      html.style.setProperty("--user-text-color", entry.hex);
    };

    // 1) The text-color ID we want to paint: prefer the active theme owner's
    //    (visiting someone else's profile), fall back to visitor's own profile
    //    when on a generic page.
    const activeTextColorId = theme?.text_color || null;

    if (activeTextColorId) {
      // We have an owner-supplied text color — apply directly without the
      // visitor's /me/profile round-trip.
      api.get("/profile/text-color-palette").then((p) => {
        if (!alive) return;
        applyById(activeTextColorId, p.data?.palette);
      }).catch(() => {});
    } else {
      // No owner override → fall back to the visitor's own preference.
      api.get("/me/profile").then((r) => {
        if (!alive) return;
        const tc = r.data?.text_color || null;
        if (!tc) { clearAll(); return; }
        api.get("/profile/text-color-palette").then((p) => {
          if (!alive) return;
          applyById(tc, p.data?.palette);
        }).catch(() => {});
      }).catch(() => {});
    }
    return () => {
      alive = false;
      clearAll();
    };
  }, [myTheme, theme?.text_color]);

  // ----- Background: stamp data-vault-bg on <body> + inject the AI-art URL -----
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    if (!body || !html) return;
    if (theme?.background_id) {
      body.setAttribute("data-vault-bg", theme.background_id);
      // Special-case: when the user picks their one-time custom AI background,
      // we serve it from the custom-studio endpoint keyed by their user_id
      // (which the provider passes through on theme.background_url).
      const isCustom = theme.background_id === "custom" && theme.background_url;
      // CRITICAL: append `?generate=false` so the <img> fetch from CSS
      // ::before NEVER silently triggers a Nano Banana generation. If the
      // bg isn't cached the request 404s and the background stays empty
      // until the prewarm finishes (and the next page-load fills the cache).
      // Without this, every visit to any page burned LLM credits.
      // Hour-bucketed `v` so regens propagate within an hour, bypassing
      // any browser cache pinned to the old immutable response.
      const vStamp = Math.floor(Date.now() / 3600000);
      // Custom background URL is stored with `/api/...` prefix already, so
      // use ORIGIN (no /api suffix) to avoid the `/api/api/...` double
      // prefix that 404'd. Catalog backgrounds get the standard API path.
      const url = isCustom
        ? `${ORIGIN}${theme.background_url}`
        : `${API}/themes/bg/${encodeURIComponent(theme.background_id)}.png?generate=false&v=${vStamp}`;
      html.style.setProperty("--vault-bg-url", `url("${url}")`);
      // Drip scheduler fills the catalog server-side now — no frontend
      // prewarm. (Old code fired POST /themes/bg/{id}/prewarm here, but
      // the backend locked that endpoint admin-only to stop guests from
      // burning LLM credits, which meant every theme apply 403'd in the
      // console.)
    } else {
      body.removeAttribute("data-vault-bg");
      html.style.removeProperty("--vault-bg-url");
    }
    return () => {
      body.removeAttribute("data-vault-bg");
      html.style.removeProperty("--vault-bg-url");
    };
  }, [theme?.background_id, theme?.background_url]);

  // ----- Audio: drive the tone engine -----
  const [playing, setPlaying] = useState(false);
  const [hidden, setHidden] = useState(false); // user dismissed the floating player
  const [volume, setVolumeState] = useState(theme?.audio_volume ?? 0.3);
  const lastSoundIdRef = useRef(null);

  // Sync volume to engine.
  useEffect(() => { toneEngine.setVolume(volume); }, [volume]);

  // When the active theme changes, decide whether to attempt autoplay.
  // Priority: sound_mix (stacked layers) > sound_id (legacy single).
  const mixKey = useMemo(
    () => (theme?.sound_mix && theme.sound_mix.length > 0
      ? theme.sound_mix.map((l) => `${l.id}:${l.volume}`).join(",")
      : ""),
    [theme?.sound_mix],
  );
  useEffect(() => {
    setHidden(false);
    const hasMix = theme?.sound_mix && theme.sound_mix.length > 0;
    if (!hasMix && !theme?.sound_id) {
      toneEngine.stop();
      setPlaying(false);
      lastSoundIdRef.current = null;
      return;
    }
    if (theme.autoplay !== false) {
      const starter = hasMix
        ? toneEngine.playMix(theme.sound_mix)
        : toneEngine.play(theme.sound_id);
      starter.then((ok) => {
        if (ok && toneEngine.isPlaying()) setPlaying(true);
      });
    } else {
      toneEngine.stop();
      setPlaying(false);
    }
    lastSoundIdRef.current = hasMix ? "__mix__" : theme.sound_id;
    return () => {
      toneEngine.stop();
      setPlaying(false);
    };
  }, [theme?.sound_id, mixKey, theme?.autoplay]);

  const soundCatalog = catalog?.sounds || [];
  const soundMeta = useMemo(
    () => soundCatalog.find((s) => s.id === theme?.sound_id) || null,
    [soundCatalog, theme?.sound_id],
  );
  // Pretty label for the floating player when the user is running a mix.
  const mixLabel = useMemo(() => {
    if (!theme?.sound_mix || theme.sound_mix.length === 0) return null;
    return theme.sound_mix
      .map((l) => soundCatalog.find((s) => s.id === l.id)?.name || l.id)
      .join(" + ");
  }, [theme?.sound_mix, soundCatalog]);

  const toggle = async () => {
    const hasMix = theme?.sound_mix && theme.sound_mix.length > 0;
    if (!hasMix && !theme?.sound_id) return;
    if (toneEngine.isPlaying()) {
      toneEngine.stop();
      setPlaying(false);
    } else {
      const ok = hasMix
        ? await toneEngine.playMix(theme.sound_mix)
        : await toneEngine.play(theme.sound_id);
      setPlaying(ok && toneEngine.isPlaying());
    }
  };

  if (!theme) return null;

  return (
    <>
      <CursorTrail trail={trail} />
      {theme.marquee_text ? <MarqueeBar text={theme.marquee_text} ownerLabel={ownerLabel} /> : null}

      {(theme.sound_id || (theme.sound_mix && theme.sound_mix.length > 0)) && !hidden ? (
        <div className="theme-player" data-testid="theme-player">
          <button
            type="button"
            className="theme-player__btn"
            onClick={toggle}
            data-testid="theme-player-toggle"
            aria-label={playing ? "Pause theme audio" : "Play theme audio"}
          >
            {playing ? <Pause size={14} /> : <Play size={14} />}
          </button>
          <div style={{ minWidth: 0 }}>
            <div className="theme-player__label">
              <Music2 size={10} style={{ display: "inline", marginRight: 4 }} />
              {ownerLabel ? `${ownerLabel} · ${mixLabel ? "mix" : "anthem"}` : (mixLabel ? "mix" : "anthem")}
            </div>
            <div className="theme-player__name" title={mixLabel || soundMeta?.name}>
              {mixLabel || soundMeta?.name || theme.sound_id}
            </div>
          </div>
          <input
            type="range"
            min={0} max={1} step={0.05}
            value={volume}
            onChange={(e) => setVolumeState(parseFloat(e.target.value))}
            className="theme-player__vol"
            data-testid="theme-player-volume"
            aria-label="Theme volume"
          />
          <button
            type="button"
            className="theme-player__close"
            onClick={() => { toneEngine.stop(); setPlaying(false); setHidden(true); }}
            data-testid="theme-player-close"
            aria-label="Hide theme player"
          >
            <X size={14} />
          </button>
        </div>
      ) : null}
    </>
  );
}


/* Pinned marquee strip. While mounted, it sets `body[data-vault-marquee="on"]`
 * so the global CSS rule pushes the sticky Navbar down by --vault-marquee-h.
 * Without that, the navbar (z-50) covers the marquee (z-60 fixes the stack
 * order; the body attr handles the layout shift). */
function MarqueeBar({ text, ownerLabel }) {
  useEffect(() => {
    document.body.setAttribute("data-vault-marquee", "on");
    return () => document.body.removeAttribute("data-vault-marquee");
  }, []);
  return (
    <div
      className="vault-marquee"
      data-testid="vault-marquee"
      aria-label={`${ownerLabel || ""} marquee`}
    >
      <div className="vault-marquee__track">
        <span>{text}</span>
        <span>{text}</span>
        <span>{text}</span>
        <span>{text}</span>
      </div>
    </div>
  );
}
